import { useQuery } from '@tanstack/react-query';
import {
  ROTULO_CANAL_COMISSAO,
  ROTULO_STATUS_IMPLANTACAO,
  formatarReais,
  formatarReaisCompacto,
  type CanalComissao,
  type EtapaFunil,
  type StatusImplantacao,
} from '@reducee/contracts';
import { Link } from 'react-router';

import { useState } from 'react';

import { chamarApi } from '../api/cliente.ts';
import { NovaOportunidade } from '../componentes/NovaOportunidade.tsx';
import { useSessao } from '../autenticacao/sessao.tsx';
import {
  CabecalhoDePagina,
  Cartao,
  Indicador,
  LinhaDeLista,
  Pilula,
  data,
} from '../componentes/Superficie.tsx';

type Bonificacao = {
  usuaria: { id: string; nome: string };
  fechadaCentavos: number;
  pagaCentavos: number;
  percentualBonificacao: number;
  bonificacaoCentavos: number;
};

type Resumo = {
  meta: {
    nome: string;
    escopo: 'GERAL' | 'INDIVIDUAL';
    valorMetaCentavos: number;
    valorFechadoCentavos: number;
    percentualAtingido: number;
    dataInicial: string;
    dataFinal: string;
    ciclos: Array<{
      nome: string;
      dataInicial: string;
      dataFinal: string;
      metaCentavos: number;
      realizadoCentavos: number;
      atual: boolean;
    }>;
  } | null;
  oportunidadesFechadas: { quantidade: number; valorMensalCentavos: number; vidas: number };
  producaoPagaCentavos: number;
  comissaoPrevistaCentavos: number | null;
  sla: { semPrimeiroContato: number; emRisco: number; estourado: number };
  tarefas: { abertas: number; vencendoHoje: number; atrasadas: number };
  prazosOperacionais: { vencidos: number; proximos: number };
  implantacoes: { abertas: number; aguardandoPrimeiroBoleto: number };
  parcelasVencidas: number;
  funil: Array<{ etapa: EtapaFunil; quantidade: number; valorMensalCentavos: number }>;
  bonificacao: Bonificacao[];
  pendenciasCriticas: Array<{
    implantacaoId: string;
    cliente: string;
    produto: string;
    operadora: string | null;
    status: StatusImplantacao;
  }>;
  comissaoPorCanal: Array<{ canal: CanalComissao; previstoCentavos: number }> | null;
};

/**
 * O pulso da operação.
 *
 * Reúne comercial, pós-venda e receita numa leitura só. A distinção que a tela
 * insiste em preservar é entre venda fechada e venda paga: a primeira mede
 * esforço, a segunda é a que vira bonificação e comissão.
 */
export function PaginaDashboard(): React.JSX.Element {
  const { usuaria } = useSessao();
  const consulta = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => chamarApi<Resumo>('/dashboard'),
  });

  const resumo = consulta.data;
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';
  const [cadastroAberto, setCadastroAberto] = useState(false);

  return (
    <section>
      <CabecalhoDePagina
        area="Visão geral"
        titulo="O pulso da operação"
        descricao={
          ehAdministradora
            ? 'Comercial, implantação e recebimentos em uma única visão.'
            : 'Seu desempenho, suas implantações e suas pendências.'
        }
        acoes={
          <>
            {ehAdministradora ? (
              <Link
                to="/implantacao"
                className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
              >
                Ver implantações
              </Link>
            ) : null}
            {ehAdministradora ? (
              <button
                type="button"
                onClick={() => setCadastroAberto(true)}
                className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro"
              >
                + Nova oportunidade
              </button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          teste="prazos-vencidos"
          rotulo="SLA vencido"
          valor={String(resumo?.prazosOperacionais.vencidos ?? '—')}
          detalhe="Movimentações/implantações"
          severidade="erro"
        />
        <Indicador
          teste="prazos-proximos"
          rotulo="SLA próximos 2 dias"
          valor={String(resumo?.prazosOperacionais.proximos ?? '—')}
          detalhe="Ações preventivas"
          severidade="alerta"
        />
        <Indicador
          teste="parcelas-vencidas"
          rotulo="Parcelas vencidas"
          valor={String(resumo?.parcelasVencidas ?? '—')}
          detalhe="Contratos ativos"
          severidade="erro"
        />
        <Indicador
          teste="tarefas-abertas"
          rotulo="Tarefas em aberto"
          valor={String(resumo?.tarefas.abertas ?? '—')}
          detalhe={`${resumo?.tarefas.atrasadas ?? 0} atrasadas · ${resumo?.tarefas.vencendoHoje ?? 0} hoje`}
          severidade="sucesso"
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Indicador
          rotulo={
            resumo?.meta
              ? resumo.meta.escopo === 'INDIVIDUAL'
                ? `Sua meta · ${resumo.meta.nome}`
                : `Meta ${resumo.meta.nome}`
              : 'Meta'
          }
          valor={resumo?.meta ? formatarReaisCompacto(resumo.meta.valorMetaCentavos) : '—'}
          detalhe={
            resumo?.meta
              ? `${data(resumo.meta.dataInicial)} → ${data(resumo.meta.dataFinal)}`
              : 'Nenhuma campanha ativa'
          }
        />
        <Indicador
          teste="oportunidades-fechadas"
          rotulo="Vendas fechadas"
          valor={
            resumo ? formatarReais(resumo.oportunidadesFechadas.valorMensalCentavos) : '—'
          }
          detalhe={
            resumo
              ? `${resumo.oportunidadesFechadas.quantidade} vendas · ${resumo.oportunidadesFechadas.vidas} vidas`
              : undefined
          }
        />
        <Indicador
          teste="producao-paga"
          rotulo="Produção paga"
          valor={resumo ? formatarReais(resumo.producaoPagaCentavos) : '—'}
          detalhe="Elegível para bonificação"
        />
        <Indicador
          teste="implantacoes-abertas"
          rotulo="Implantações abertas"
          valor={String(resumo?.implantacoes.abertas ?? '—')}
          detalhe={`${resumo?.implantacoes.aguardandoPrimeiroBoleto ?? 0} aguardando 1º boleto`}
        />
        <Indicador
          teste="comissao-prevista"
          rotulo="Comissão prevista"
          valor={
            resumo?.comissaoPrevistaCentavos === null || resumo === undefined
              ? '—'
              : formatarReais(resumo.comissaoPrevistaCentavos)
          }
          detalhe={
            resumo?.comissaoPrevistaCentavos === null
              ? 'Receita da corretora'
              : 'Receitas comerciais projetadas'
          }
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Cartao
          className="lg:col-span-2"
          titulo={resumo?.meta?.nome ?? 'Campanha'}
          descricao={
            resumo?.meta?.escopo === 'INDIVIDUAL'
              ? 'Suas vendas fechadas contra a sua parte da meta, em cada ciclo.'
              : 'Vendas fechadas acumuladas na campanha atual.'
          }
        >
          {resumo?.meta ? (
            <>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-texto-suave">Atingimento</span>
                <span data-teste="percentual-meta" className="font-semibold">
                  {resumo.meta.percentualAtingido.toFixed(2).replace('.', ',')}%
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-marinho-suave">
                <div
                  className="h-full rounded-full bg-dourado"
                  style={{ width: `${Math.min(100, resumo.meta.percentualAtingido)}%` }}
                />
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {resumo.meta.ciclos.map((ciclo) => (
                  <div
                    key={ciclo.nome}
                    className={`rounded-lg border p-3 text-xs ${
                      ciclo.atual
                        ? 'border-marinho-claro bg-marinho-suave'
                        : 'border-linha bg-superficie'
                    }`}
                  >
                    <p className="font-semibold text-marinho">
                      {ciclo.nome}
                      {ciclo.atual ? ' · atual' : ''}
                    </p>
                    <p className="text-texto-suave">
                      {data(ciclo.dataInicial)} – {data(ciclo.dataFinal)}
                    </p>
                    <p className="mt-1 font-medium">
                      {formatarReais(ciclo.realizadoCentavos)} / {formatarReais(ciclo.metaCentavos)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-texto-suave">Nenhuma campanha ativa no momento.</p>
          )}
        </Cartao>

        <Cartao
          titulo="Bonificação do mês"
          descricao="Considera somente vendas fechadas com 1º boleto pago."
        >
          {resumo && resumo.bonificacao.length > 0 ? (
            resumo.bonificacao.map((linha) => (
              <LinhaDeLista
                key={linha.usuaria.id}
                titulo={linha.usuaria.nome}
                detalhe={`${formatarReais(linha.pagaCentavos)} de produção paga`}
                valor={`${linha.percentualBonificacao}%`}
                complemento={formatarReais(linha.bonificacaoCentavos)}
              />
            ))
          ) : (
            <p className="text-texto-suave">Sem produção paga neste mês.</p>
          )}
        </Cartao>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
        <Cartao
          titulo="Pendências críticas"
          descricao="O que impede vendas de virarem produção válida."
        >
          {resumo && resumo.pendenciasCriticas.length > 0 ? (
            resumo.pendenciasCriticas.map((pendencia) => (
              <LinhaDeLista
                key={pendencia.implantacaoId}
                titulo={pendencia.cliente}
                detalhe={[pendencia.operadora, pendencia.produto].filter(Boolean).join(' · ')}
                valor={<Pilula tom="alerta">{ROTULO_STATUS_IMPLANTACAO[pendencia.status]}</Pilula>}
              />
            ))
          ) : (
            <p className="py-4.5 text-xs text-texto-suave">Nenhuma pendência crítica.</p>
          )}
        </Cartao>

        {/* Receita da corretora é informação restrita: só a Administradora a vê,
          * e por isso a API só a envia para ela. */}
        {resumo?.comissaoPorCanal ? (
          <Cartao
            titulo="Comissão por canal"
            descricao="Previsão baseada nas regras salvas em cada venda."
          >
            {resumo.comissaoPorCanal.map((linha) => (
              <LinhaDeLista
                key={linha.canal}
                titulo={ROTULO_CANAL_COMISSAO[linha.canal]}
                valor={formatarReais(linha.previstoCentavos)}
              />
            ))}
          </Cartao>
        ) : null}
      </div>

      {cadastroAberto ? <NovaOportunidade aoFechar={() => setCadastroAberto(false)} /> : null}
    </section>
  );
}
